import { Badge, Box, IconButton, Paper } from '@mui/material';
import { useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';
import { getColorByRole } from '../../../../utils/generics';
import { useApplicationContext } from '../../../context/ApplicationContext/ApplicationContext';
import { useTranslation } from '../../../translation/TranslationContext';
import { useChatContext } from '../../contexts/chat/chat.context';
import { findChatByIdInGroup } from '../../contexts/chat/hooks/useChats/reducer/chatListReducer.utils';
import { invertChatRole, isPrivateChat } from '../../utils/chat.utils';
import ChatAvatar from '../ChatAvatar/ChatAvatar';
import { formatBrandStoreChat } from '../ChatConfigViews/components/ChatContainerHeader/chatHeader.utils';
import MessageNotificationSnackbar from './components/MessageNotificationSnackbar/MessageNotificationSnackbar';

interface ChatBubbleProps {
  chatId: string;
}

const useStyles = makeStyles<void, 'paperButton'>()((_theme, _params, classes) => ({
  paperButton: {
    width: '22px',
    height: '22px',
    display: 'none',
    fontSize: '14px',
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconButton: {
    '&:hover': {
      backgroundColor: 'transparent',
    },

    [`&:hover .${classes.paperButton}`]: {
      display: 'flex',
    },
  },
  avatar: {
    border: '1px solid #0000001A',
  },
}));

const ChatBubble = ({ chatId }: ChatBubbleProps) => {
  const { classes: styles } = useStyles();

  const { translate } = useTranslation();

  const { isStore, userRole } = useApplicationContext();

  const {
    rooms,
    getLoadedChatById,
    onChangeSelectedChat,
    removeChatFromMinimized,
    getTotalUnreadMessageToChat,
  } = useChatContext();

  const lastMessageToChat = useMemo(() => {
    return getLoadedChatById(chatId)?.lastMessage;
  }, [getLoadedChatById, chatId]);

  const currentChat = useMemo(() => {
    const groupWithChat = rooms.find((room) => room.chatIdsPresentInThisGrouping.includes(chatId));

    if (!groupWithChat) {
      return null;
    }

    return findChatByIdInGroup(groupWithChat, chatId);
  }, [rooms, chatId]);

  const titleAndLogo = useMemo(() => {
    if (!currentChat) {
      return {
        title: '',
        logoUrl: '',
      };
    }

    return formatBrandStoreChat({ chat: currentChat, isStore, translate });
  }, [currentChat]);

  return (
    <Box
      test-id="chat-bubble-wrapper"
      sx={{
        display: 'flex',
        gap: '12px',
        flexDirection: 'row',
      }}
    >
      <IconButton
        size="small"
        disableRipple
        className={styles.iconButton}
        onClick={() => onChangeSelectedChat(chatId, true)}
      >
        <Badge
          overlap="rectangular"
          badgeContent={
            <Paper
              className={styles.paperButton}
              onClick={(e) => {
                e.stopPropagation();
                removeChatFromMinimized(chatId);
              }}
            >
              x
            </Paper>
          }
        >
          <Badge
            overlap="rectangular"
            badgeContent={getTotalUnreadMessageToChat(chatId)}
            classes={{ badge: styles.avatar }}
            color="primary"
          >
            <ChatAvatar
              size={5.5}
              src={titleAndLogo.logoUrl}
              title={titleAndLogo.title}
              chatType={currentChat?.type}
              backgroundColor={getColorByRole({
                userRole: invertChatRole(
                  !!currentChat && isPrivateChat(currentChat.type),
                  isStore,
                  userRole,
                ),
                withSrc: !!titleAndLogo.logoUrl,
                transparentWhenHasSrc: false,
              })}
            />
          </Badge>
        </Badge>
      </IconButton>
      <MessageNotificationSnackbar
        messageInfo={
          lastMessageToChat
            ? {
                text: lastMessageToChat.body,
                updatedAt: lastMessageToChat.updatedAt,
              }
            : undefined
        }
      />
    </Box>
  );
};

export default ChatBubble;
